/**
 * 스크롤 페이드 - 스크롤 칸의 끝을 흐려서 "여기 더 있다"를 알린다.
 *
 * ## 왜 고정 마스크를 버렸나
 *
 * 전에는 CSS 한 줄이 전부였다.
 *
 * ```css
 * mask-image: linear-gradient(to bottom, #000 calc(100% - 40px), transparent);
 * ```
 *
 * 이 마스크는 **스크롤 위치를 모른다.** 그래서 바닥까지 내려도 아래 40px이 계속 흐렸고,
 * 결과 화면에서는 하필 그 자리에 있는 마지막 버튼이 반쯤 지워져 보였다. 신호여야 할 것이
 * 목적지를 가리는 물건이 된 셈이다. **더 볼 것이 없으면 페이드도 없어야 한다.**
 *
 * 페이드를 없애는 선택지는 없다. 이 앱은 터치 기기에서 스크롤바를 숨기고, 단계 목록은 10단계
 * 중 6~7줄만 보인다. 신호가 사라지면 사람이 게임의 절반을 못 찾는다. 고칠 것은 페이드의
 * 존재가 아니라 **페이드가 스크롤 위치를 모른다는 것**이다.
 *
 * ## 지금의 규칙
 *
 * 페이드 두께 = 그 방향으로 남은 스크롤 거리(최대 `FADE_MAX_PX`).
 *
 * 켜고 끄는 대신 **남은 거리에 비례해 얇아진다.** 이유는 경계에서 튀지 않기 위해서다.
 * 40px 임계로 껐다 켰다 하면 바닥 근처에서 마스크가 한 번에 사라지며 마지막 줄이 번쩍인다.
 * 남은 거리를 그대로 쓰면 페이드는 바닥에 닿는 순간 자연스럽게 0이 되고, 별도의 전이
 * 애니메이션도 필요 없다. 관찰 게임에서 화면이 까닭 없이 움직이는 것은 그 자체로 해악이다.
 *
 * ## 왜 클래스와 커스텀 프로퍼티 둘 다인가
 *
 * 두께는 연속값이라 커스텀 프로퍼티(`--sf-fade-*`)로 넘긴다. 하지만 두께가 0일 때도
 * `mask-image`가 걸려 있으면 브라우저는 그 요소를 계속 별도 레이어로 합성한다. 스크롤
 * 칸에서 그 비용은 공짜가 아니다. 그래서 **마스크를 걸지 말지**는 상태 클래스
 * (`is-fade-top` / `is-fade-bottom`)가 정하고, CSS는 그 클래스가 붙었을 때만 마스크를 쓴다.
 *
 * CSS 쪽 계약은 이 네 줄이 전부다(`levelSelect.css`, `gameHud.css`가 같은 모양으로 쓴다).
 *
 * ```css
 * .x-scroll.is-fade-top,
 * .x-scroll.is-fade-bottom {
 *   mask-image: linear-gradient(to bottom,
 *     transparent 0, #000 var(--sf-fade-top, 0px),
 *     #000 calc(100% - var(--sf-fade-bottom, 0px)), transparent 100%);
 * }
 * ```
 *
 * 숫자는 이 파일에만 있다. CSS는 두께를 모르고, 이 파일은 색을 모른다.
 *
 * ## 왜 IntersectionObserver가 아닌가
 *
 * 센티넬 + IO는 "끝에 닿았다/아니다"라는 **이진 신호**만 준다. 위에서 정한 비례 두께는
 * 연속값이라 IO로는 만들 수 없다. 대신 스크롤 이벤트는 passive로 달고 rAF로 묶어서
 * 프레임당 읽기 한 번·쓰기 한 번으로 제한하고, 값이 그대로면 쓰기조차 건너뛴다.
 */

/**
 * 페이드가 가장 짙어졌을 때의 두께.
 * 이 값이 여기에만 있는 이유는 CSS가 `--sf-fade-*`로 받아 쓰기 때문이다 - 숫자가 두 곳에
 * 적히면 반드시 갈라진다.
 */
const FADE_MAX_PX = 40

/**
 * 끝에 닿았다고 볼 오차.
 * 브라우저 확대나 소수 픽셀 레이아웃에서 `scrollTop`이 정수로 떨어지지 않는다. 이 여유가
 * 없으면 "바닥인데 0.5px 남았다"며 페이드가 남고, 그게 정확히 이번에 고치는 버그다.
 */
const EDGE_EPSILON_PX = 1

/**
 * 아직 아무 두께도 쓰지 않았다는 표시. 실제 두께는 음수가 될 수 없으므로 겹치지 않는다.
 * 첫 갱신이 반드시 DOM에 쓰이게 하려고 둔다.
 */
const NOT_WRITTEN = -1

/** 위로 더 볼 것이 있을 때 붙는다. */
export const FADE_TOP_CLASS = 'is-fade-top'

/** 아래로 더 볼 것이 있을 때 붙는다. */
export const FADE_BOTTOM_CLASS = 'is-fade-bottom'

/** 위쪽 페이드 두께(px). CSS가 `var()`로 읽는다. */
export const FADE_TOP_PROP = '--sf-fade-top'

/** 아래쪽 페이드 두께(px). CSS가 `var()`로 읽는다. */
export const FADE_BOTTOM_PROP = '--sf-fade-bottom'

export interface ScrollFadeOptions {
  /** 페이드 최대 두께(px). 기본값 `FADE_MAX_PX`. 예외적으로 좁은 칸과 테스트를 위해서만 연다. */
  readonly maxFadePx?: number
}

/**
 * `element`의 스크롤 위치를 따라가며 위/아래 페이드를 갱신한다.
 *
 * @returns 정리 함수. 리스너·관찰자를 떼고 이 유틸이 붙인 클래스와 커스텀 프로퍼티까지
 *   되돌린다. 화면을 파기할 때 **반드시** 부를 것 - 안 부르면 관찰자가 죽은 노드를 붙잡는다.
 *   여러 번 불러도 안전하다.
 */
export function attachScrollFade(element: HTMLElement, options: ScrollFadeOptions = {}): () => void {
  const maxFade = Math.max(0, options.maxFadePx ?? FADE_MAX_PX)

  let disposed = false
  let frame = 0

  /* 마지막으로 DOM에 쓴 두께. 같은 값이면 다시 쓰지 않는다 - 스크롤 중에는 대부분 같다. */
  let writtenTopPx = NOT_WRITTEN
  let writtenBottomPx = NOT_WRITTEN

  function update(): void {
    if (disposed) return

    const { scrollTop, scrollHeight, clientHeight } = element
    /*
     * 위아래 페이드가 각각 칸 높이의 절반을 넘으면 두 그라디언트가 서로를 먹어서 가운데까지
     * 흐려진다. 짧은 칸(가로로 눕힌 폰의 안내문)에서 실제로 일어난다.
     */
    const limit = Math.max(0, Math.min(maxFade, clientHeight / 2))

    const topPx = fadeFor(scrollTop, limit)
    if (topPx !== writtenTopPx) {
      writtenTopPx = topPx
      writeFade(element, FADE_TOP_CLASS, FADE_TOP_PROP, topPx)
    }

    /* 남은 아래 거리. iOS 고무줄 스크롤에서는 음수가 되는데 `fadeFor`가 0으로 접는다. */
    const bottomPx = fadeFor(scrollHeight - clientHeight - scrollTop, limit)
    if (bottomPx !== writtenBottomPx) {
      writtenBottomPx = bottomPx
      writeFade(element, FADE_BOTTOM_CLASS, FADE_BOTTOM_PROP, bottomPx)
    }
  }

  /*
   * 스크롤·리사이즈·변형이 한 프레임에 몰려도 갱신은 한 번이다. 읽기(scrollTop 등)와
   * 쓰기(class·style)를 rAF 안에 함께 두면 프레임당 강제 레이아웃도 한 번으로 끝난다.
   */
  const schedule = (): void => {
    if (disposed || frame !== 0) return
    frame = requestAnimationFrame(() => {
      frame = 0
      update()
    })
  }

  element.addEventListener('scroll', schedule, { passive: true })

  /* 창 회전·칸 크기 변화. 관찰 시작 시점에 한 번 불리므로 늦게 배치되는 화면도 스스로 맞춘다. */
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null
  resizeObserver?.observe(element)

  /*
   * 내용 변화. ResizeObserver는 **칸의 박스**만 본다 - 목록에 줄이 늘어 `scrollHeight`가
   * 커지는 것은 칸 크기 변화가 아니라서 잡히지 않는다. 로캘 전환으로 목록을 다시 그리는
   * 경로가 실제로 있으므로 이쪽도 관찰해야 한다.
   *
   * `attributes`를 켜지 않는 이유는 되먹임이다 - 이 유틸이 쓰는 것이 `element`의 class와
   * style뿐이라, 속성까지 보면 자기 쓰기가 자기 갱신을 부른다.
   */
  const mutationObserver =
    typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null
  mutationObserver?.observe(element, { childList: true, subtree: true, characterData: true })

  /*
   * 웹폰트가 늦게 도착하면 줄 높이가 바뀐다. 그건 칸 리사이즈도 DOM 변형도 아니라서 위
   * 두 관찰자 어디에도 안 걸린다. 이 앱은 표시용 글꼴(G마켓 산스)을 늦게 싣는다.
   */
  void document.fonts?.ready.then(schedule).catch(() => {})

  /* 첫 그림은 rAF를 기다리지 않는다. 한 프레임이라도 페이드 없이 뜨면 그게 깜빡임으로 보인다. */
  update()

  return () => {
    if (disposed) return
    disposed = true

    if (frame !== 0) {
      cancelAnimationFrame(frame)
      frame = 0
    }
    element.removeEventListener('scroll', schedule)
    resizeObserver?.disconnect()
    mutationObserver?.disconnect()

    /* 붙인 것은 전부 되돌린다. 노드를 재사용하는 화면에서 남은 클래스가 유령 페이드가 된다. */
    element.classList.remove(FADE_TOP_CLASS, FADE_BOTTOM_CLASS)
    element.style.removeProperty(FADE_TOP_PROP)
    element.style.removeProperty(FADE_BOTTOM_PROP)
  }
}

/**
 * 남은 스크롤 거리를 페이드 두께로 바꾼다.
 * 오차 이내면 0(= 끝에 닿음), 그 밖에는 `limit`까지 거리에 비례한다.
 *
 * 정수로 반올림하는 이유는 위의 "같으면 안 쓴다" 판정을 살리기 위해서다. 소수 두께를 그대로
 * 쓰면 스크롤 한 프레임마다 값이 달라져 판정이 늘 실패하고, 0.4px 차이는 마스크에서 보이지도
 * 않는다.
 */
function fadeFor(distance: number, limit: number): number {
  if (!Number.isFinite(distance) || distance < EDGE_EPSILON_PX) return 0
  return Math.round(Math.min(distance, limit))
}

/** 두께가 0이면 마스크 자체를 끈다(클래스 제거). 합성 레이어를 남기지 않기 위해서다. */
function writeFade(element: HTMLElement, className: string, property: string, px: number): void {
  element.classList.toggle(className, px > 0)
  element.style.setProperty(property, `${px}px`)
}
